/**
 * Sentry init — frontend.
 *
 * No-op until REACT_APP_SENTRY_DSN is set. The DSN is the only required value;
 * everything else has sensible defaults for a closed-beta React/SPA app.
 *
 * Why an init module? Keeping Sentry wiring out of App.js means we can:
 *   - early-exit cleanly when the DSN is empty
 *   - colocate axios + ErrorBoundary integrations in one place
 *   - swap to a different error tracker later without touching App.js
 */
import * as Sentry from "@sentry/react";

const DSN = process.env.REACT_APP_SENTRY_DSN || "";
const ENV = process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV || "development";
const RELEASE = process.env.REACT_APP_VERSION || "fitsme-web@local";

let _initialized = false;

export const initSentry = () => {
    if (_initialized) return;
    if (!DSN) {
        // Closed-beta default: no DSN = no-op. Logs once so dev knows.
        if (typeof window !== "undefined" && window.__FITSME_DEV_LOGGED__ !== true) {
            console.debug("[sentry] REACT_APP_SENTRY_DSN not set — error tracking disabled.");
            window.__FITSME_DEV_LOGGED__ = true;
        }
        return;
    }
    Sentry.init({
        dsn: DSN,
        environment: ENV,
        release: RELEASE,
        // Tracing — sample 10% so we don't burn through the free tier.
        tracesSampleRate: 0.1,
        // Replays — only on errors. PII is masked by default.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        // We mostly care about real production crashes — strip dev-only noise.
        ignoreErrors: [
            "ResizeObserver loop limit exceeded",
            "ResizeObserver loop completed with undelivered notifications",
            // Network errors are surfaced in-app via toasts; no need to alert.
            /^NetworkError when attempting to fetch resource\.?$/,
            /^Failed to fetch$/,
        ],
    });
    _initialized = true;
};

export const captureError = (err, context = {}) => {
    if (!_initialized) return;
    Sentry.captureException(err, { extra: context });
};

/** Wraps an axios instance to forward 5xx + network failures to Sentry. */
export const attachAxiosInterceptor = (axiosInstance) => {
    if (!axiosInstance || typeof axiosInstance.interceptors?.response?.use !== "function") return;
    axiosInstance.interceptors.response.use(
        (resp) => resp,
        (error) => {
            const status = error?.response?.status;
            // Only escalate genuine server-side failures + network drops.
            if (!status || status >= 500) {
                captureError(error, {
                    url: error?.config?.url,
                    method: error?.config?.method,
                    status,
                });
            }
            return Promise.reject(error);
        },
    );
};

export { Sentry };
